import React, { Fragment, useContext } from "react";
import Bulk from "./../Bulk/Bulk";
import Basket from "./../Basket/Basket";
import style from "./../Order.module.scss";
import { Container, Row, Col, Input } from "reactstrap";
import { accountProps, customFetchAssetInfo, customFetchAccountInfo } from "./commons";
import { OrderPriceType, TimeFrame, ProductType, Type, Mode, OrderStatus } from "../../../../utils/Order/Order";
import { Field, Symbol, Account, Numeric, Combobox, TimeForce, PriceType } from "../Field/Field";
import { UserType } from "../../../../utils/Helper";
import { Dispatch } from "../store/store";
import { onChange, onAccountChange } from "../Order";
import QuantityHelper from "../Helper/QuantityHelper";
import Booktype from "../Field/Booktype";

const Options = ({ maxDecimals, updatePrice, newSymbol, booktypes, platformRole, opsViewAccountName }) => {
  const { dispatch, state, services } = useContext(Dispatch);
  const { order, fields, ui, entities } = state;
  const { editable, errors, readonly, hiddens } = fields;

  const accountInfo = (props, updateOrderValues) =>
    customFetchAccountInfo(
      dispatch,
      services.account,
      {
        ...order,
        CurrentAccount: {
          ...order.CurrentAccount,
          ...props,
        },
      },
      updateOrderValues
    );

  let quantities = [];
  let quantityTitle = <span>Quantity</span>;
  if (ui.type == Type.Default || ui.type == Type.Chained || ui.type == Type.Cross) {
    if (order.CurrentAccount.Day) {
      quantities = order.CurrentAccount.Day.map((day) => {
        return {
          date: day.Date,
          status: day.Status,
          quantity: parseFloat(day.AssignedQty),
        };
      });
    }
    quantityTitle = (
      <Fragment>
        <span>Quantity</span>
        {quantities.length > 0 && order.OrderId > 0 && (
          <QuantityHelper quantities={quantities} name="general">
            <i className="icon-info icons" />
          </QuantityHelper>
        )}
      </Fragment>
    );
  }

  const quantityMaxDecimals = parseInt(order.SubAssetClassDecimalPlaces, 10) || 0;

  return (
    <div className={style.form}>
      <Container>
        <Row>
          {/* Left */}
          <Col xs={12} lg={6}>
            <Field label={"Symbol"} error={errors.AssetTicker}>
              <Symbol
                form={order.AssetClassForm}
                onChange={(v) => onChange(dispatch, v)}
                placeholder={"Symbol"}
                disabled={readonly.AssetTicker || (order.offer && +order.offer.OfferId > 0)}
                invalid={!!errors.AssetTicker}
                value={order.AssetTicker}
                description={order.AssetDescription}
                provider={order.AssetSecurityProvider}
                assetid={order.AssetId}
                attrId={"AssetId"}
                attrTicker={"AssetTicker"}
                attrDescription={"AssetDescription"}
                attrProvider={"AssetSecurityProvider"}
                {...services.symbol}
                assetInfo={() => customFetchAssetInfo(dispatch, services.symbol, order)}
                updatePrice={updatePrice}
                maxChar={30}
              />
            </Field>

            <Row>
              <Col lg={6}>
                <Field label={"Call/Put"} error={errors.isCall}>
                  <Combobox
                    value={order.isCall}
                    items={[
                      { id: 1, description: "Call" },
                      { id: 2, description: "Put" },
                    ]}
                    disabled={readonly.AssetTicker}
                    onChange={(v) => onChange(dispatch, { isCall: v })}
                    emptyLabel={"Call/Put"}
                    style={{ background: "#edf9ff", fontWeight: 500 }}
                  />
                </Field>
              </Col>
              <Col lg={6}>
                <Field label={"Expires"} error={errors.expiration}>
                  <Input
                    type={"date"}
                    placeholder={"Expires"}
                    onChange={(v) => onChange(dispatch, { expiration: v })}
                    value={order.expiration}
                    disabled={!!readonly.AssetTicker}
                    style={{ padding: "6px 4px" }}
                    required
                  />
                </Field>
              </Col>
            </Row>

            <Row>
              <Col lg={6}>
                {/* ##### Quantity ###### */}
                <Field label={quantityTitle} error={errors.OrderQuantity}>
                  <Numeric
                    value={order.OrderQuantity}
                    placeholder={"Quantity"}
                    dataTypeDecimalName="quantity"
                    disabled={readonly.OrderQuantity}
                    onChange={(v) => onAccountChange(dispatch, { Quantity: v })}
                    invalid={!!errors.OrderQuantity}
                  />
                </Field>
              </Col>

              <Col lg={6}>
                <Field label={"Strike"} error={errors.strikePrice}>
                  <Numeric
                    value={order.strikePrice}
                    placeholder={"Strike"}
                    disabled={readonly.AssetTicker}
                    onChange={(v) => onChange(dispatch, { strikePrice: v })}
                    invalid={!!errors.strikePrice}
                    decimalScale={maxDecimals}
                    dataTypeDecimalName="price"
                  />
                </Field>
              </Col>
            </Row>

            <Row>
              {/* ##### Trade Currency ###### */}
              <Col lg={6}>
                <Field label={"Trade Currency"} error={errors.OrderCurrencySymbol}>
                  <Input
                    type="text"
                    placeholder="Trade Currency"
                    value={order.OrderCurrencySymbol || ""}
                    disabled
                  />
                </Field>
              </Col>
              {/* ##### Settlement Currency ###### */}
              <Col lg={6}>
                <Field label={"Settlement Currency"} error={errors.OrderSettlementCurrencySymbol}>
                  <Combobox
                    value={order.OrderSettlementCurrencyId || 0}
                    items={entities.settlementCurrencies}
                    disabled={readonly.OrderSettlementCurrencySymbol}
                    invalid={!!errors.OrderSettlementCurrencySymbol}
                    onChange={(v) => onChange(dispatch, { OrderSettlementCurrencyId: v })}
                    emptyLabel={"Settlement Currency"}
                  />
                </Field>
              </Col>
            </Row>

            <PriceType
              type={order.OrderType}
              form={ProductType.OPTION}
              currency={order.OrderCurrencySymbol}
              values={{
                limPrice: order.OrderPrice,
                stopPrice: order.OrderStopPrice,
              }}
              oldValues={{
                limPrice: order.HasOriginalOrReplacingData? order.OriginalOrReplacingData.OrderPrice : order.OrderPrice,
                stopPrice: order.HasOriginalOrReplacingData? order.OriginalOrReplacingData.OrderStopPrice : order.OrderStopPrice,
              }}
              editable={{
                limPrice: editable.OrderPrice && (order.OrderStatus === OrderStatus.ACKED),
                stopPrice: editable.OrderStopPrice && (order.OrderStatus === OrderStatus.ACKED),
              }}
              errors={{
                type: errors.OrderType,
                limPrice: errors.OrderPrice,
                stopPrice: errors.OrderStopPrice,
              }}
              readonly={{
                type: readonly.OrderType,
                limPrice: readonly.OrderPrice,
                stopPrice: readonly.OrderStopPrice,
              }}
              onChange={(v) => onChange(dispatch, v)}
              maxDecimals={maxDecimals}
              newSymbol={newSymbol}
            />

            {/* ##### TimeForce ###### */}
            <TimeForce
              date={order.OrderGTD}
              timeForce={order.OrderTimeInForce}
              errors={{
                date: errors.OrderGTD,
                timeForce: errors.OrderTimeInForce,
              }}
              readonly={{
                date: readonly.OrderGTD,
                timeForce: readonly.OrderTimeInForce,
              }}
              onChange={(v) => onChange(dispatch, v)}
            />
            {(ui.userRole === "Trader" || (ui.userRole !== "Trader" && ui.mode !== Mode.EDIT)) && <Booktype
              booktypes={booktypes}
              booktype={order.BookType}
              balancetype={order.CurrentAccount.BalanceType}
              errors={{
                BookType: errors.BookType,
                BalanceType: errors.BalanceType
              }}
              readonly={{
                BookType: readonly.BookType,
                BalanceType: errors.BalanceType
              }}
              onChange={(v) => onChange(dispatch, v)}
              onAccountChange={(v) => onAccountChange(dispatch, v)}
            />}

            {/* ##### Custody ###### */}
            {/*
            (ui.type == Type.Default || ui.type == Type.Chained || ui.type == Type.Cross)
              && !hiddens.RequiresSpecialCustody && (
              <Field>
                <Switch 
                  value={order.CurrentAccount.RequiresSpecialCustody}
                  label={'Custody'}
                  disabled={readonly.RequiresSpecialCustody}
                  onChange={v => onChange(dispatch, {RequiresSpecialCustody: v})}
                />
              </Field>
            )
            */}
          </Col>

          {/* Right */}
          <Col xs={12} lg={6}>
            {/* ##### Account ###### */}
            {!ui.isBulk && !ui.isBasket && (
              <Account
                userType={ui.userRole}
                platformRole={platformRole}
                onChange={(v) => onAccountChange(dispatch, v)}
                balanceReady={!!order.AssetId}
                dispatch={dispatch}
                {...services.account}
                accountInfo={accountInfo}
                {...accountProps({ order, readonly, errors })}
                hiddens={{
                  Quantity: true,
                  ...hiddens,
                }}
                opsViewAccountName={opsViewAccountName}
              />
            )}
            {ui.isBulk && (
              <Bulk
                balanceReady={!!order.AssetId}
                onChange={(v) => onAccountChange(dispatch, v)}
                accounts={order.Accounts || []}
                isReadOnly={ui.mode == Mode.READONLY}
                canModifyComposition={ui.mode == Mode.EDIT}
                readonly={readonly}
                error={errors.Accounts}
                account={order.CurrentAccount}
                {...services.account}
                accountInfo={accountInfo}
                hiddens={hiddens}
              />
            )}

            {ui.isBasket && (
              <Basket
                balanceReady={!!order.AssetId}
                onChange={(v) => onAccountChange(dispatch, v)}
                accounts={order.Accounts || []}
                isReadOnly={ui.mode == Mode.READONLY}
                canModifyComposition={ui.mode == Mode.EDIT}
                readonly={readonly}
                error={errors.Accounts}
                account={order.CurrentAccount}
                {...services.account}
                accountInfo={accountInfo}
                hiddens={hiddens}
              />
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export const defaultValues = () => {
  return {
    OrderSide: "Buy",
    OrderOpenClose: "Open",
    BookType: "Self"
  };
};

export const validations = (order, userType) => {
  const filteredBankAccounts = order.CurrentAccount.clientBankAccounts
  ? order.CurrentAccount.clientBankAccounts.filter(
      (account) => account.currencyISO === order.OrderCurrencySymbol
    )
  : [];

  const isMultiAccounts = order.OrderHasMultipleAccounts;

  return {
    OrderQuantity: { empty: !isMultiAccounts },
    AssetTicker: { empty: true, autosuggestId: "AssetId" },
    OrderType: { empty: true },
    OrderPrice: {
      empty: order.OrderType == OrderPriceType.LIMIT || order.OrderType == OrderPriceType.STOPLIMIT,
    },
    OrderStopPrice: {
      empty: order.OrderType == OrderPriceType.STOP || order.OrderType == OrderPriceType.STOPLIMIT,
    },
    OrderTimeInForce: { empty: true },
    OrderGTD: {
      empty: order.OrderTimeInForce == TimeFrame.GTD,
    },
    OrderSettlementCurrencySymbol: { empty: true },

    // The following properties are from an account.
    AccountName: {
      empty: true,
      accountLevel: true,
    },
    clientBankAccounts: {
      noBankAccountSelected: filteredBankAccounts && filteredBankAccounts.length > 0 && (order.CurrentAccount.clientBankAccount == null || order.CurrentAccount.clientBankAccount == "") && !order.CurrentAccount.useCashAccount,
    },
    HolderName: {
      empty: userType !== UserType.Trader,
      accountLevel: true,
    },
    Quantity: {
      empty: true,
      accountLevel: true,
    },
    Instruction: {
      empty: userType !== UserType.Trader,
      accountLevel: true,
    },
    MarkupType: {
      empty: userType !== UserType.Trader,
      accountLevel: true,
    },
    CommissionType: {
      empty: userType !== UserType.Trader,
      accountLevel: true,
    },
  };
};

export default Options;
